import { Injectable } from '@angular/core';
import { HttpServiceBundle } from '@app/core/services/base/http-service-bundle.service';
import { Observable } from 'rxjs';
import {
    BaseHttpWithBundleService,
    OpData
} from '@app/core/services/base/base-service-with-bundle.service';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { DRResponse } from '@app/shared/models/response';
import { UserRole } from '@app/shared/models/user-role';
import { BaseList, BaseWithIdList } from '@app/shared/models/list';
import { UserAssignment } from '@app/shared/models/user-assignment';
import { BackendUriUnits } from '@app/core/services/base/backend-uri-units';
import { Department } from '@app/shared/models/department';
import { FileUtilityService } from '@app/core/services/file-utility.service';
import { ClientCategory, ClientCategoryEnum } from '@app/shared/models/client-category';
import { Message } from '@app/shared/models/message';

@Injectable({ providedIn: 'root' })
export class ClientService extends BaseHttpWithBundleService {

    constructor(httpServiceBundle: HttpServiceBundle,
                protected fileUtilityService: FileUtilityService) {
        super(httpServiceBundle, true);
        // Setting a new uri because this is not an SMP call
        this.uri += `/${BackendUriUnits.CLIENTS}`;
    }

    public getClientRoles(clientId: string): Observable<BaseWithIdList<UserRole>> {
        const uri = `${this.uri}/${BackendUriUnits.CLIENT}/roles`;
        return this.http.get<DRResponse<BaseWithIdList<UserRole>>>(uri, { params: { clientId: clientId }})
                        .pipe(map(result => new BaseWithIdList<UserRole>(UserRole, result.data)));
    }

    public getUserAssignments(clientId: string, includeOffices: boolean): Observable<BaseList<UserAssignment>> {
        const uri = `${this.uri}/${BackendUriUnits.MEMBER}/${BackendUriUnits.CLIENTS}/${clientId}/user_assignments`;
        return this.http.get<DRResponse<BaseList<UserAssignment>>>(uri, { params: { include_offices: includeOffices.toString() }})
                        .pipe(map(result => new BaseList<UserAssignment>(UserAssignment, result.data)));
    }

    public getDepartments(): Observable<BaseWithIdList<Department>> {
        const uri = `${this.uri}/${BackendUriUnits.MEMBER}/${BackendUriUnits.CLIENTS}`;
        return this.http.get<any>(uri)
                        .pipe(map(result => new BaseWithIdList<Department>(Department, result.data.clients)));
    }

    public createDepartment(department: Department): Observable<Department> {
        const uri = `${this.uri}/${BackendUriUnits.MEMBER}/${BackendUriUnits.CLIENTS}`;
        return this.http.post<DRResponse<Department>>(uri, { companyName: department.companyname }) // Very strange expectations from the backend
                        .pipe(map(result => new Department(result.data)));
    }

    public patchDepartment(department: Department | string, patches: OpData[]): Observable<Department> {
        const uri = `${this.uri}/${BackendUriUnits.MEMBER}/${BackendUriUnits.CLIENTS}/${department instanceof Department ? department.id : department}`;
        return this.http.patch<DRResponse<Department>>(uri, patches)
                        .pipe(map(result => new Department(result.data)));
    }

    public deleteDepartment(department: Department | string): Observable<boolean> {
        const uri = `${this.uri}/${BackendUriUnits.MEMBER}/${BackendUriUnits.CLIENTS}/${department instanceof Department ? department.id : department}`;
        return this.http.delete<DRResponse<boolean>>(uri)
                        .pipe(map(result => result.status === 'SUCCESS'));
    }

    public openOrganigram(): void {
        const organigramUrl = `${environment.apiUrl}/${BackendUriUnits.API}/${BackendUriUnits.CLIENTS}/organigram${this.fileUtilityService.addToken()}`;
        window.open(organigramUrl, '_blank');
    }

    public getCategories(clientCategory: ClientCategoryEnum): Observable<ClientCategory> {
        const uri = `${this.uri}/service/categories/${clientCategory}`;
        return this.http.get<DRResponse<ClientCategory>>(uri)
                        .pipe(map(result => new ClientCategory(result.data)));
    }

    public getMessages(): Observable<BaseWithIdList<Message>> {
        const uri = `${this.uri}/${BackendUriUnits.MEMBER}/${BackendUriUnits.MESSAGES}`;
        return this.http.get<DRResponse<BaseWithIdList<Message>>>(uri)
                        .pipe(map(result => new BaseWithIdList<Message>(Message, result.data)));
    }

    public patchMessage(message: Message | string, patches: OpData[]): Observable<Message> {
        const uri = `${this.uri}/${BackendUriUnits.MEMBER}/${BackendUriUnits.MESSAGES}/${message instanceof Message ? message.id : message}`;
        return this.http.patch<DRResponse<Message>>(uri, patches)
                        .pipe(map(result => new Message(result.data)));
    }
}
